(() => {
  if (customElements.get('boxr-shipping-destination')) return;
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function waitForCart() {
    // Taste debounces quantity changes for 300 ms before starting the cart request.
    await pause(400);
    const deadline = Date.now() + 10000;
    while (document.querySelector('.cart__items--disabled, product-form .loading:not([hidden])')) {
      if (Date.now() > deadline) throw new Error('Your cart is still updating. Please wait and try again.');
      await pause(100);
    }
  }

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors || cart.status) {
      throw new Error(typeof cart.errors === 'string' ? cart.errors : cart.description || 'We could not update your cart. Please try again.');
    }
    return cart;
  }

  class BoxrShippingDestination extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.select = this.querySelector('[data-destination-select]');
      this.status = this.querySelector('[data-destination-status]');
      this.busy = false;
      this.select?.addEventListener('change', () => this.updateDestination(), { signal: this.controller.signal });
      document.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('[data-boxr-quote-link]');
        const checkout = (this.dataset.mode === 'international' || this.busy) && event.target.closest('button[name="checkout"]');
        if (!link && !checkout) return;
        event.preventDefault();
        this.openQuote(link || checkout);
      }, { signal: this.controller.signal });
      document.addEventListener('submit', (event) => {
        if (this.dataset.mode !== 'international' || event.submitter?.name !== 'checkout') return;
        event.preventDefault();
        this.openQuote(event.submitter);
      }, { signal: this.controller.signal });
    }

    async updateDestination(destination = this.select.value) {
      if (this.busy) return false;
      if (!['US', 'international'].includes(destination)) return false;
      this.select.value = destination;
      this.busy = true;
      this.select.disabled = true;
      this.status.textContent = 'Updating shipping destination…';
      try {
        await waitForCart();
        await cartRequest(`${this.dataset.cartUrl}/update.js`, { attributes: { boxr_shipping_destination: destination } });
        if (destination === 'US' && new URL(window.location.href).searchParams.get('view') === 'international-request') {
          window.location.assign(this.dataset.cartUrl);
        } else {
          window.location.reload();
        }
        return true;
      } catch (error) {
        this.select.value = this.dataset.mode;
        this.status.textContent = error.message;
        this.select.disabled = false;
        this.busy = false;
        return false;
      }
    }

    async openQuote(trigger) {
      if (this.busy) return;
      this.busy = true;
      const originalLabel = trigger.textContent;
      trigger.setAttribute('aria-disabled', 'true');
      trigger.textContent = 'Preparing your request…';
      const container = trigger.closest('cart-drawer') || document.querySelector('cart-items');
      const inputs = Array.from(container?.querySelectorAll('input[name="updates[]"]') || []);
      // Capture the visible quantities before a pending section render replaces these inputs.
      const quantities = inputs.map((input) => ({ key: input.dataset.quantityLineKey, quantity: Number(input.value), valid: input.checkValidity() }));
      try {
        if (quantities.some((item) => !item.valid || !Number.isInteger(item.quantity) || item.quantity < 0)) {
          inputs.find((input) => !input.checkValidity())?.reportValidity();
          throw new Error('Please check your product quantities before requesting a quote.');
        }
        await waitForCart();
        const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('We could not load your cart. Please try again.');
        let cart = await response.json();
        for (const requested of quantities) {
          const item = cart.items.find((line) => line.key === requested.key);
          if (!item) {
            if (requested.quantity === 0) continue;
            throw new Error('Your cart changed while updating. Please refresh it and review your selection.');
          }
          if (item.quantity === requested.quantity) continue;
          // change.js validates inventory and identifies a line by its key, not a shared variant ID.
          cart = await cartRequest(`${this.dataset.cartUrl}/change.js`, { id: requested.key, quantity: requested.quantity });
          if (requested.quantity > 0 && !cart.items.some((line) => line.quantity === requested.quantity && line.variant_id === item.variant_id && JSON.stringify(line.properties) === JSON.stringify(item.properties) && line.selling_plan_allocation?.selling_plan?.id === item.selling_plan_allocation?.selling_plan?.id)) {
            throw new Error('Your requested quantity is not available. Please refresh your cart and review it.');
          }
        }
        window.location.assign(trigger.getAttribute('href') || this.dataset.quoteUrl);
      } catch (error) {
        this.status.textContent = error.message;
        let message = trigger.parentElement.querySelector('[data-boxr-quote-error]');
        if (!message) {
          message = document.createElement('p');
          message.className = 'boxr-quote-error';
          message.dataset.boxrQuoteError = '';
          message.setAttribute('role', 'alert');
          trigger.parentElement.appendChild(message);
        }
        message.textContent = error.message;
        trigger.textContent = originalLabel;
        trigger.removeAttribute('aria-disabled');
        this.busy = false;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  }
  customElements.define('boxr-shipping-destination', BoxrShippingDestination);
})();
